import { Fragment, useEffect, useMemo, useState } from 'react';
import { getStripeProducts, StripeProduct } from '../../api/subscription';
import { clearCheckoutAddonsStorage } from '../../utils/checkoutAddons';
import { firePaymentTypeChange, firePlanChange } from '../../utils/dataLayer';
import './AppUpsellBloods.scss';
import bannerImg from '../../assets/images/BannerCoreVitalsMembership.png';

const timelineItems = [
  { title: 'Today', subtitle: 'Book' },
  { title: 'Day 2', subtitle: 'Nurse visit' },
  { title: 'Day 5', subtitle: 'Results' },
  { title: 'Day 10', subtitle: 'Action plan' },
];
const defaultTestCounts = [1, 2, 3, 4];

interface SelectedPlanData {
  type: 'coreVitals';
  testCount: number | null;
  product: StripeProduct | null;
  productId: string | null;
  paymentType: 'monthly' | 'yearly';
}

function AppUpsellBloods() {
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [isProductsLoading, setIsProductsLoading] = useState<boolean>(true);
  const [selectedTestCount, setSelectedTestCount] = useState<number | null>(null);
  const [selectedBilling, setSelectedBilling] = useState<'monthly' | 'yearly'>('monthly');
  const [selectedTimelineDay, setSelectedTimelineDay] = useState('Today');

  const coreVitalsProducts = useMemo(() => {
    return products
      .filter((product) => product.active && product.key.startsWith('core_') && !product.key.startsWith('core_plus_'))
      .sort((a, b) => {
        const aNum = parseInt(a.key.match(/\d+/)?.[0] || '0', 10);
        const bNum = parseInt(b.key.match(/\d+/)?.[0] || '0', 10);
        return aNum - bNum;
      });
  }, [products]);

  const availableTestCounts = useMemo(() => {
    const countsFromProducts = coreVitalsProducts
      .map((product) => parseInt(product.key.match(/\d+/)?.[0] || '0', 10))
      .filter((count) => count > 0);

    return countsFromProducts.length ? countsFromProducts : defaultTestCounts;
  }, [coreVitalsProducts]);

  const selectedProduct = useMemo(() => {
    if (!selectedTestCount) return null;
    return coreVitalsProducts.find((product) => product.key === `core_${selectedTestCount}_test`) || null;
  }, [coreVitalsProducts, selectedTestCount]);

  const selectedPrice = useMemo(() => {
    if (!selectedProduct) return 38;
    const amount = selectedBilling === 'monthly' ? selectedProduct.monthly_amount : selectedProduct.yearly_amount;
    return amount ?? 38;
  }, [selectedBilling, selectedProduct]);

  const featureItems = [
    '100+ Biomarkers - hormones, heart, metabolic & more',
    'Nurse visits you - results in 5 days',
    'AI chat across your full blood history',
    'Doctor-built action plan & supplement protocol',
    'Biological age tracking',
  ];

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setIsProductsLoading(true);
        const response = await getStripeProducts();
        setProducts(response.products || []);
      } catch {
        setProducts([]);
      } finally {
        setIsProductsLoading(false);
      }
    };

    loadProducts();
  }, []);


  useEffect(() => {
    clearCheckoutAddonsStorage();
  }, []);

  useEffect(() => {
    if (!availableTestCounts.length) return;
    if (selectedTestCount && availableTestCounts.includes(selectedTestCount)) return;
    setSelectedTestCount(availableTestCounts[0]);
  }, [availableTestCounts, selectedTestCount]);

  useEffect(() => {
    if (!selectedTestCount || !selectedProduct) return;
    firePlanChange('coreVitals', selectedTestCount, selectedBilling, selectedProduct);
  }, [selectedBilling, selectedProduct, selectedTestCount]);

  const timelineActiveIndex = timelineItems.findIndex((t) => t.title === selectedTimelineDay);
  const timelineSafeIndex = timelineActiveIndex >= 0 ? timelineActiveIndex : 0;
  const timelineLastIndex = timelineItems.length - 1;
  const accentConnectorIndex =
    timelineLastIndex <= 0
      ? 0
      : timelineSafeIndex >= timelineLastIndex
        ? timelineLastIndex - 1
        : timelineSafeIndex;

  const handleBillingChange = (billing: 'monthly' | 'yearly') => {
    setSelectedBilling(billing);
    firePaymentTypeChange(billing, 'coreVitals', selectedTestCount);
  };

  const handleBecomeMember = () => {
    if (!selectedProduct || !selectedTestCount) return;

    const productId =
      selectedBilling === 'monthly'
        ? selectedProduct.stripe_price_monthly_id
        : selectedProduct.stripe_price_yearly_id;

    const planData: SelectedPlanData = {
      type: 'coreVitals',
      testCount: selectedTestCount,
      product: selectedProduct,
      productId,
      paymentType: selectedBilling,
    };

    localStorage.setItem('selectedPlan', JSON.stringify(planData));
    clearCheckoutAddonsStorage();
    const tokenParam = new URLSearchParams(window.location.search).get('token');
    window.location.href = tokenParam ? `/complete-subscription?token=${tokenParam}` : '/complete-subscription';
  };

  return (
    <div className='upsell-bloods-page'>
      <div className='upsell-card'>
        <div className='upsell-layout'>
          <div className='upsell-left'>
            <p className='membership-label'>CoreVitals Membership</p>
            <h2>Get healthier.</h2>
            <p className='accent-line'>Grow younger</p>

            <section className='card-block'>
              <h3>Blood tests per year</h3>
              <p className='subtext'>
                <img src='/icons/Check Circle.png' alt='' aria-hidden='true' />
                <span>Includes free nurse blood draw at home</span>
              </p>

              <div className='button-row'>
                {availableTestCounts.map((count) => (
                  <button
                    key={count}
                    className={selectedTestCount === count ? 'active' : ''}
                    type='button'
                    onClick={() => setSelectedTestCount(count)}
                    aria-pressed={selectedTestCount === count}
                    disabled={isProductsLoading}
                  >
                    {count} test{count > 1 ? 's' : ''}
                  </button>
                ))}
              </div>

              <h3 className='billing-heading'>Billing</h3>
              <div className='billing-toggle'>
                <button
                  type='button'
                  className={selectedBilling === 'monthly' ? 'active' : ''}
                  onClick={() => handleBillingChange('monthly')}
                  aria-pressed={selectedBilling === 'monthly'}
                >
                  Monthly
                </button>
                <button
                  type='button'
                  className={selectedBilling === 'yearly' ? 'active' : ''}
                  onClick={() => handleBillingChange('yearly')}
                  aria-pressed={selectedBilling === 'yearly'}
                >
                  Yearly
                </button>
              </div>

              <div className='price'>
                <span className='amount'>£{selectedPrice.toLocaleString()}</span>
                <span className='period'>/{selectedBilling === 'yearly' ? 'year' : 'month'}</span>
              </div>
            </section>

            <section className='feature-block mobile-feature-block'>
              {featureItems.map((feature) => (
                <div className='feature-item' key={feature}>
                  <img src='/icons/Check Circle.png' alt='' aria-hidden='true' />
                  <p>{feature}</p>
                </div>
              ))}
            </section>

            <section className='timeline'>
              {timelineItems.map((item, index) => (
                <Fragment key={item.title}>
                  <button
                    type='button'
                    className={[
                      selectedTimelineDay === item.title ? 'active' : '',
                      timelineActiveIndex >= 0 && index === timelineActiveIndex + 1 ? 'timeline-step--next' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => setSelectedTimelineDay(item.title)}
                    aria-pressed={selectedTimelineDay === item.title}
                  >
                    <p>{item.title}</p>
                    <span>{item.subtitle}</span>
                  </button>
                  {index < timelineItems.length - 1 && (
                    <span
                      className={[
                        'timeline-connector',
                        index === accentConnectorIndex ? 'timeline-connector--accent' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      aria-hidden='true'
                    >
                      <span className='timeline-connector-line' />
                    </span>
                  )}
                </Fragment>
              ))}
            </section>

            <button
              type='button'
              className='primary-cta'
              onClick={handleBecomeMember}
              disabled={!selectedProduct || !selectedTestCount || isProductsLoading}
            >
              Become a member
            </button>

            <div className='footer-points'>
              <button type='button' className='footer-point-btn'>
                <img src='/icons/circle2.png' alt='' aria-hidden='true' />
                <span>Cancel anytime</span>
              </button>
              <button type='button' className='footer-point-btn'>
                <img src='/icons/Dialog 2.png' alt='' aria-hidden='true' />
                <span>Unlimited GP chat</span>
              </button>
            </div>
          </div>

          <aside className='order-summary'>
            <h3>Order summary</h3>
            <img src={bannerImg} alt='CoreVitals Membership' className='summary-banner' />
            <h4>
              CoreVitals Membership {selectedTestCount ? `${selectedTestCount} test${selectedTestCount > 1 ? 's' : ''}` : ''}
            </h4>
            <p className='summary-copy'>Ongoing health tracking without imaging - up to 4x blood tests/year</p>

            <section className='feature-block'>
              {featureItems.map((feature) => (
                <div className='feature-item' key={feature}>
                  <img src='/icons/Check Circle.png' alt='' aria-hidden='true' />
                  <p>{feature}</p>
                </div>
              ))}
            </section>

            <div className='summary-total'>
              <span>Total</span>
              <strong>
                £{selectedPrice.toLocaleString()}<small>/{selectedBilling === 'yearly' ? 'year' : 'month'}</small>
              </strong>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

export default AppUpsellBloods;
